import '@components/marketplace/sms-credits/sms-credits.scss';

import { AppstoreOutlined } from '@ant-design/icons';
import { SmsAutoRechargeForm } from '@components/marketplace/sms-credits/sms-auto-recharge-form';
import { SmsCreditBalanceSummary } from '@components/marketplace/sms-credits/sms-credit-balance-summary';
import { SmsCreditPackageCard } from '@components/marketplace/sms-credits/sms-credit-package-card';
import { SmsCreditPurchaseHistory } from '@components/marketplace/sms-credits/sms-credit-purchase-history';
import { SmsCreditsSectionTitle } from '@components/marketplace/sms-credits/sms-credits-section-title';
import { Payment } from '@interfaces/payment';
import { ISmsCreditBalance, ISmsCreditPackage } from '@interfaces/sms-credits';
import { useApiUrl, useCustom, useNavigation } from '@refinedev/core';
import Response from '@responses/response';
import { Alert, Button, Card, Result } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { isTenantAdmin } from '../../accessControlProvider';

export const SmsCreditsIndex: React.FC = () => {
    const { t } = useTranslation(['marketplace']);
    const apiUrl = useApiUrl();
    const { push } = useNavigation();
    const [refreshKey, setRefreshKey] = useState(0);
    const enabled = isTenantAdmin;
    const delayedRefresh = useRef<ReturnType<typeof setTimeout>>();
    useEffect(() => () => clearTimeout(delayedRefresh.current), []);

    const {
        data: balanceData,
        isLoading: isLoadingBalance,
        isError: balanceError,
        refetch: refetchBalance,
    } = useCustom<Response<ISmsCreditBalance>>({
        url: `${apiUrl}/v1/sms-credits/balance`,
        method: 'get',
        errorNotification: false,
        queryOptions: { enabled, retry: 1 },
    });
    const { data: packageData } = useCustom<Response<ISmsCreditPackage[]>>({
        url: `${apiUrl}/v1/sms-credits/packages`,
        method: 'get',
        queryOptions: { enabled },
    });
    const { data: paymentMethods } = useCustom<Payment.IPaymentMethod[]>({
        url: `${apiUrl}/v1/users/payment-methods`,
        method: 'get',
        config: { filters: [{ field: 'type', operator: 'eq', value: 'finix' }] },
        queryOptions: { enabled },
    });

    if (!enabled) {
        return (
            <Result
                status="403"
                title={t('smsCredits.adminOnly')}
                extra={<a onClick={() => push('/marketplace')}>{t('actions.back')}</a>}
            />
        );
    }

    const packages = [...(packageData?.data?.data ?? [])].sort((a, b) => a.sort - b.sort);
    const defaultCard = paymentMethods?.data?.find((m) => m.isDefault) ?? null;
    // Value hints: savings are measured against the smallest package's per-credit price; the cheapest rate is "best value".
    const rate = (p: ISmsCreditPackage) => p.price / p.credits;
    const baseRate = packages.length ? rate(packages[0]) : 0;
    const bestRate = packages.length > 1 ? Math.min(...packages.map(rate)) : null;

    const handlePurchased = () => {
        refetchBalance();
        setRefreshKey((k) => k + 1);
        // A 'pending' purchase settles shortly after: refresh again so credits/status show up.
        clearTimeout(delayedRefresh.current);
        delayedRefresh.current = setTimeout(() => {
            refetchBalance();
            setRefreshKey((k) => k + 1);
        }, 8000);
    };

    return (
        <section
            className="sms-credits-page"
            style={{ display: 'flex', flexDirection: 'column', gap: 24 }}
        >
            <p className="sms-credits-subtitle">{t('smsCredits.subtitle')}</p>
            <SmsCreditBalanceSummary
                balance={balanceData?.data?.data}
                isLoading={isLoadingBalance}
                isUnavailable={balanceError}
                onRetry={refetchBalance}
            />
            <Card
                title={
                    <SmsCreditsSectionTitle icon={<AppstoreOutlined />}>
                        {t('smsCredits.packages')}
                    </SmsCreditsSectionTitle>
                }
            >
                {/* The card is known before the modal: no surprise "add a card" step after choosing a package. */}
                <Alert
                    style={{ marginBottom: 16 }}
                    type={defaultCard ? 'info' : 'warning'}
                    showIcon
                    message={
                        defaultCard
                            ? t('smsCredits.payment.card', {
                                  brand: defaultCard.brand ?? t('smsCredits.purchase.card'),
                                  last4: defaultCard.last4,
                              })
                            : t('smsCredits.payment.noCard')
                    }
                    action={
                        <Button size="small" onClick={() => push('/setting/billing')}>
                            {t(
                                defaultCard
                                    ? 'smsCredits.payment.manage'
                                    : 'smsCredits.payment.add',
                            )}
                        </Button>
                    }
                />
                <div className="sms-credit-package-grid">
                    {packages.map((pkg) => (
                        <SmsCreditPackageCard
                            key={pkg.id}
                            pkg={pkg}
                            defaultCard={defaultCard}
                            onPurchased={handlePurchased}
                            savingsPercent={
                                baseRate > 0 ? Math.round((1 - rate(pkg) / baseRate) * 100) : 0
                            }
                            isBestValue={bestRate != null && rate(pkg) === bestRate}
                        />
                    ))}
                </div>
                <p className="sms-credits-footnote">{t('smsCredits.contactAbove')}</p>
            </Card>
            <SmsAutoRechargeForm packages={packages} defaultCard={defaultCard} />
            <SmsCreditPurchaseHistory refreshKey={refreshKey} />
        </section>
    );
};
